import type React from 'react';
import { Icon } from '../../components/ui/Icon';
import { StatusDot } from '../../components/ui/StatusDot';
import { ICONO_ESTADO_SENSOR } from '../../components/domain/iconosDominio';
import type { Sensor } from '../../../domain/entities/Sensor';
import { formatearAntiguedad } from '../../utils/formato';
import estilos from './Monitoreo.module.css';

interface TarjetaSensorProps {
  sensor: Sensor;
  /** Conexión del nodo al que pertenece; `null` si no puede saberse. */
  nodoEnLinea?: boolean | null;
  /** Muestra el nodo en la línea de detalle. Se omite al agrupar por nodo. */
  mostrarNodo?: boolean;
  /** Acaba de cambiar de valor: se resalta un momento. */
  reciente?: boolean;
  onAbrir?: (sensor: Sensor) => void;
}

/**
 * Tarjeta de un sensor con su última lectura.
 *
 * El estado se transmite por icono, color y texto a la vez: quien no distinga
 * los colores sigue sabiendo si algo va mal.
 */
export function TarjetaSensor({
  sensor,
  nodoEnLinea,
  mostrarNodo = true,
  reciente = false,
  onAbrir,
}: TarjetaSensorProps) {
  const estado = sensor.estado;
  const desconectado = nodoEnLinea === false;

  const detalle = [
    mostrarNodo ? sensor.nodoNombre : null,
    formatearAntiguedad(sensor.lectura?.timestamp),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article
      className={[
        estilos.tarjeta,
        estado.valor === 'critico' ? estilos.tarjetaCritica : '',
        desconectado ? estilos.tarjetaDesconectada : '',
        reciente ? estilos.tarjetaReciente : '',
        onAbrir ? estilos.tarjetaPulsable : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={
        onAbrir
          ? `${sensor.nombre} · ${estado.etiqueta} — pulsa para ver el detalle`
          : `${sensor.nombre} · ${estado.etiqueta}`
      }
      /*
       * Pulsable de verdad, no un `div` con `onClick`: con `role` y `tabIndex`
       * se llega con el tabulador y responde a Intro y a la barra, que es como
       * navega quien no usa ratón.
       */
      {...(onAbrir
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: () => onAbrir(sensor),
            onKeyDown: (evento: React.KeyboardEvent) => {
              if (evento.key === 'Enter' || evento.key === ' ') {
                evento.preventDefault();
                onAbrir(sensor);
              }
            },
          }
        : {})}
    >
      <span
        className={estilos.icono}
        style={{ background: `color-mix(in srgb, ${estado.color} 15%, transparent)` }}
      >
        <Icon nombre={ICONO_ESTADO_SENSOR[estado.valor]} tamano={20} color={estado.color} />
      </span>

      <div className={estilos.datos}>
        <div className={estilos.nombre}>{sensor.nombre}</div>
        <div className={estilos.meta}>
          {desconectado ? 'Nodo desconectado' : detalle || estado.etiqueta}
        </div>
      </div>

      <div className={estilos.valor}>
        {sensor.esDigital && sensor.tieneLectura ? (
          <StatusDot
            color={estado.color}
            encendido={sensor.lectura?.esEncendido}
            texto={sensor.valorNumericoMostrable}
            tamano={9}
            etiqueta={`${sensor.nombre}: ${sensor.valorNumericoMostrable}`}
          />
        ) : (
          <span className={estilos.valorNumero}>
            {sensor.valorNumericoMostrable}
            {sensor.unidadMostrable && (
              <span className={estilos.valorUnidad}>{sensor.unidadMostrable}</span>
            )}
          </span>
        )}
      </div>
    </article>
  );
}
